import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import redCrossIcon from '../../static/icons/red-cross.svg';
import search14grayIcon from '../../static/img/search14gray.svg';
import previewIcon from '../../static/img/preview.svg';
import PreviewDialog from '../Shared/Dialogs/preview/previewCard';
import CommentBankDialog from '../Shared/Dialogs/commentBank';
import SuccessCriteriaDialog from '../Shared/Dialogs/successCriteria';
import ShortAnswerDialog from '../Shared/Dialogs/shortAnswer';
import { getCommentBank, getSuccessCriteria, getShortAnswer, getMarkingMethodology } from '../../service';
import SmallLoader from '../Loader/SmallLoader';

function DropdownMenuMultiselect({
  options = [],
  selectedValues: selectedValuesProp,
  onChange,
  placeholder = 'Select all that apply',
  getOptionLabel = (opt) => {
    if (opt != null && typeof opt === 'object') {
      const label =
        opt.label ??
        opt.subjectName ??
        opt.name ??
        opt.title ??
        opt.subjectCode ??
        opt.code ??
        opt.value ??
        '';
      return String(label ?? '');
    }
    return String(opt ?? '');
  },
  getOptionValue = (opt) => {
    if (opt != null && typeof opt === 'object') {
      return (
        opt.id ??
        opt.value ??
        opt.code ??
        opt.subjectCode ??
        JSON.stringify(opt)
      );
    }
    return opt;
  },
  disabled = false,
  alwaysWhite = false,
  showSearch = false,
  showCheckboxes = true,
  showAllOption = true,
  singleSelect = false,
  leadingIcon = null,
  compactDisplay = false,
  showAllWhenAllSelected = false,
  listAllWhenEmpty = false,
  triggerFontSize = '14px',
  triggerPadding = '8px 12px',
  triggerWidth,
  dropdownHeader = '',
  dropdownSubtitle = '',
  headerBackground = CI_HEADER_BG,
  allLabel = 'All',
  minDropdownWidth = '320px',
  showPreviewIcon = false,
  previewType = 'rubric',
  groupByArchived = false,
  activeLabel = 'Active Classes',
  archivedLabel = 'Archived Classes',
}) {
  const selectedValues = Array.isArray(selectedValuesProp) ? selectedValuesProp : [];
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: '320px' });
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewLoadingTarget, setPreviewLoadingTarget] = useState(null);
  const [allDeselected, setAllDeselected] = useState(false);

  useEffect(() => {
    if (selectedValues.length > 0) setAllDeselected(false);
  }, [selectedValues.length]);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const safeOptions = Array.isArray(options) ? options.filter((opt) => opt != null) : [];

  const updatePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const parsed = parseInt(String(minDropdownWidth).replace(/\D/g, ''), 10);
    const minW = Number.isFinite(parsed) && parsed > 0 ? parsed : 320;
    const isMobile = window.innerWidth <= 768;
    const responsiveMinW = isMobile ? Math.min(minW, window.innerWidth - 16) : minW;
    const width = Math.max(rect.width, responsiveMinW);

    const pad = 8;
    const left = Math.min(Math.max(rect.left, pad), Math.max(pad, window.innerWidth - width - pad));
    setDropdownPos({ top: rect.bottom + 4, left, width: `${width}px` });
  }, [minDropdownWidth]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    updatePosition();
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return;
    const onScrollOrResize = () => updatePosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (dropdownRef.current?.contains(e.target)) return;
      setIsOpen(false);
      setSearchQuery('');
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, showSearch]);

  const isValueSelected = (val) =>
    selectedValues.some((v) => String(v ?? '') === String(val ?? ''));

  const allValues = safeOptions.map((opt) => getOptionValue(opt));

  const allSelected =
    !singleSelect &&
    !allDeselected &&
    safeOptions.length > 0 &&
    (selectedValues.length === 0 || allValues.every((v) => isValueSelected(v)));

  const q = searchQuery.trim().toLowerCase();
  const filteredOptions =
    !q
      ? safeOptions
      : safeOptions.filter((opt) =>
          String(getOptionLabel(opt) ?? '')
            .toLowerCase()
            .includes(q)
        );

  // Group options by archived status if enabled
  const groupedOptions = groupByArchived
    ? {
        active: filteredOptions.filter((opt) => !opt.isArchived),
        archived: filteredOptions.filter((opt) => opt.isArchived),
      }
    : null;

  const activeValues = groupedOptions ? groupedOptions.active.map((opt) => getOptionValue(opt)) : [];
  const archivedValues = groupedOptions ? groupedOptions.archived.map((opt) => getOptionValue(opt)) : [];

  const allActiveSelected =
    groupedOptions &&
    activeValues.length > 0 &&
    !allDeselected &&
    (selectedValues.length === 0 || activeValues.some((v) => isValueSelected(v)));

  const allArchivedSelected =
    groupedOptions &&
    archivedValues.length > 0 &&
    !allDeselected &&
    (selectedValues.length === 0 || archivedValues.some((v) => isValueSelected(v)));

  const handleToggleAll = (e) => {
    e.stopPropagation();
    if (allSelected) {
      // Deselect all
      setAllDeselected(true);
      onChange([]);
    } else {
      // Select all
      setAllDeselected(false);
      onChange([]);
    }
  };

  const handleToggleOption = (option, e) => {
    e.stopPropagation();
    const value = getOptionValue(option);
    if (singleSelect) {
      if (isValueSelected(value)) onChange([]);
      else onChange([value]);
      setIsOpen(false);
      setSearchQuery('');
      return;
    }

    if (allDeselected) {
      setAllDeselected(false);
      onChange([value]);
      return;
    }

    if (selectedValues.length === 0) {
      onChange(allValues.filter((v) => String(v ?? '') !== String(value ?? '')));
      return;
    }

    if (isValueSelected(value)) {
      const next = selectedValues.filter((v) => String(v ?? '') !== String(value ?? ''));
      if (next.length === 0) {
        setAllDeselected(true);
      }
      onChange(next);
    } else {
      const next = [...selectedValues, value];
      if (next.length === allValues.length) {
        onChange([]);
      } else {
        onChange(next);
      }
    }
  };

  const handleRemove = (valueToRemove, e) => {
    e.stopPropagation();
    const next = selectedValues.filter((v) => String(v ?? '') !== String(valueToRemove ?? ''));
    if (next.length === 0) {
      setAllDeselected(true);
    }
    onChange(next);
  };

  const handleToggleGroup = (groupValues, isGroupSelected, e) => {
    e.stopPropagation();
    if (isGroupSelected) {
      // Deselect all items in this group
      if (selectedValues.length === 0 && !allDeselected) {
        // Currently "All Classes" is selected, deselecting this group means select the other group
        const otherValues = allValues.filter(
          (v) => !groupValues.some((gv) => String(gv ?? '') === String(v ?? ''))
        );
        if (otherValues.length === 0) {
          // Deselecting everything
          setAllDeselected(true);
        }
        onChange(otherValues);
      } else {
        const next = selectedValues.filter(
          (v) => !groupValues.some((gv) => String(gv ?? '') === String(v ?? ''))
        );
        if (next.length === 0) {
          setAllDeselected(true);
        }
        onChange(next);
      }
    } else {
      // Select all items in this group
      setAllDeselected(false);
      if (selectedValues.length === 0 && !allDeselected) {
        // Currently "All Classes" is selected (not just empty)
        // Clicking a group when "All" is selected doesn't make sense - already selected
        return;
      }

      // Add all group values to selection
      const newValues = [...selectedValues];
      groupValues.forEach((gv) => {
        if (!isValueSelected(gv)) {
          newValues.push(gv);
        }
      });
      // Check if we've selected everything - convert to "All" state
      if (newValues.length === allValues.length) {
        onChange([]);
      } else {
        onChange(newValues);
      }
    }
  };

  const getPreviewId = (option, type = null) => {
    if (option != null && typeof option === 'object') {
      if (type === 'rubric' && option.markingCriteriaId != null) {
        return option.markingCriteriaId;
      }
      return option.id ?? option._id ?? option.value ?? getOptionValue(option);
    }
    return option;
  };

  const fetchMarkingToolDetails = async (id, type) => {
    if (id == null || id === '') return null;
    try {
      let data = null;
      if (type === 'commentbank') {
        data = await getCommentBank(id);
      } else if (type === 'successcriteria') {
        data = await getSuccessCriteria(id);
      } else if (type === 'shortanswer') {
        data = await getShortAnswer(id);
      } else if (type === 'rubric') {
        data = await getMarkingMethodology(id);
      }
      return data;
    } catch (error) {
      return null;
    }
  };

  const normalizePreviewData = (data, fallback, type) => {
    let normalized = Array.isArray(data)
      ? data.find((item) => String(item?.id ?? '') === String(fallback?.id ?? '')) || data[0]
      : data;

    if (
      type === 'rubric' &&
      normalized &&
      typeof normalized === 'object' &&
      Array.isArray(normalized.markingCriterias)
    ) {
      normalized =
        normalized.markingCriterias.find(
          (item) => String(item?.id ?? '') === String(fallback?.id ?? '')
        ) || normalized.markingCriterias[0];
    }

    if (normalized && typeof normalized === 'object') {
      if (type === 'rubric') {
        return {
          ...normalized,
          type: normalized.type || 'RUBRICS',
          criterias: Array.isArray(normalized.criterias) ? normalized.criterias : [],
        };
      }
      return normalized;
    }

    if (type === 'commentbank') {
      return fallback || { smartComments: [] };
    }
    if (type === 'successcriteria') {
      return fallback || { title: 'Untitled Success Criteria', criterias: [] };
    }
    if (type === 'shortanswer') {
      return fallback || { title: 'Untitled Short Answer', highestMarks: 0, criterias: [] };
    }
    if (type === 'rubric') {
      return {
        ...(fallback || {}),
        type: fallback?.type || 'RUBRICS',
        criterias: Array.isArray(fallback?.criterias) ? fallback.criterias : [],
      };
    }
    return fallback || { type: 'RUBRICS', criterias: [] };
  };

  const handlePreviewAll = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsLoadingPreview(true);
    setPreviewLoadingTarget('all');

    try {
      const detailsPromises = safeOptions.map((opt) => fetchMarkingToolDetails(getPreviewId(opt, previewType), previewType));
      const allDetails = await Promise.all(detailsPromises);

      let combinedData = { id: 'all', title: 'All ' + (allLabel || 'Items') };

      if (previewType === 'rubric') {
        combinedData.criterias = allDetails.flatMap((detail) => normalizePreviewData(detail, null, previewType)?.criterias || []);
      } else if (previewType === 'commentbank') {
        combinedData.smartComments = allDetails.flatMap((detail) => normalizePreviewData(detail, null, previewType)?.smartComments || []);
      } else if (previewType === 'successcriteria') {
        combinedData.criterias = allDetails.flatMap((detail) => normalizePreviewData(detail, null, previewType)?.criterias || []);
      } else if (previewType === 'shortanswer') {
        combinedData.criterias = allDetails.flatMap((detail) => normalizePreviewData(detail, null, previewType)?.criterias || []);
      }

      setPreviewData(combinedData);
      setShowPreviewDialog(true);
    } catch (error) {
      setPreviewData(normalizePreviewData(null, null, previewType));
      setShowPreviewDialog(true);
    } finally {
      setIsLoadingPreview(false);
      setPreviewLoadingTarget(null);
    }
  };

  const handlePreviewOption = async (option, e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsLoadingPreview(true);
    setPreviewLoadingTarget(String(getPreviewId(option, previewType)));

    try {
      const details = await fetchMarkingToolDetails(getPreviewId(option, previewType), previewType);
      setPreviewData(normalizePreviewData(details, option, previewType));
      setShowPreviewDialog(true);
    } catch (error) {
      setPreviewData(normalizePreviewData(null, option, previewType));
      setShowPreviewDialog(true);
    } finally {
      setIsLoadingPreview(false);
      setPreviewLoadingTarget(null);
    }
  };

  const getDisplayLabel = (value) => {
    if (value != null && typeof value === 'object') {
      return String(
        value.label ??
          value.subjectName ??
          value.name ??
          value.title ??
          value.subjectCode ??
          value.value ??
          ''
      );
    }
    const option = safeOptions.find((opt) => {
      const optVal = getOptionValue(opt);
      return optVal === value || String(optVal ?? '') === String(value ?? '');
    });
    if (option != null) return String(getOptionLabel(option) ?? '');
    return String(getOptionLabel(value) ?? '');
  };

  const handleOpenToggle = (e) => {
    e.stopPropagation();
    if (disabled) return;
    setIsOpen((o) => !o);
    if (!isOpen) setSearchQuery('');
  };

  const showHeader = Boolean(dropdownHeader || dropdownSubtitle);

  const renderTriggerLabel = () => {
    if (compactDisplay) {
      if (showAllWhenAllSelected && allSelected) {
        return (
          <CompactLabelContainer>
            <CompactLabel $triggerFontSize={triggerFontSize}>
              {allLabel}
            </CompactLabel>
          </CompactLabelContainer>
        );
      }
      // JED-1066: on the class picker an empty selection means "all classes".
      // Name them like an explicit selection ("Class A +3 more") rather than
      // falling through to the greyed placeholder, which reads as "nothing
      // selected" when the page is in fact showing every class. Deliberately
      // keyed off an empty selection rather than allSelected: deselecting
      // everything also leaves the page showing all classes, so the trigger
      // should say so either way.
      if (listAllWhenEmpty && !singleSelect && selectedValues.length === 0 && allValues.length > 0) {
        const restOfAll = allValues.length - 1;
        return (
          <CompactLabelContainer>
            <CompactLabel $triggerFontSize={triggerFontSize}>
              {getDisplayLabel(allValues[0])}
            </CompactLabel>
            {restOfAll > 0 ? <OverflowHint> +{restOfAll} more</OverflowHint> : null}
          </CompactLabelContainer>
        );
      }
    }
    if (selectedValues.length === 0) {
      return <SelectTrigger $triggerFontSize={triggerFontSize}><span>{placeholder}</span></SelectTrigger>;
    }
    if (compactDisplay) {
      const rest = selectedValues.length - 1;
      return (
        <CompactLabelContainer>
          <CompactLabel $triggerFontSize={triggerFontSize}>
            {getDisplayLabel(selectedValues[0])}
          </CompactLabel>
          {rest > 0 ? <OverflowHint> +{rest} more</OverflowHint> : null}
        </CompactLabelContainer>
      );
    }
    return (
      <ChipsContainer>
        {selectedValues.map((value, index) => (
          <Chip key={`${value}-${index}`}>
            <ChipText>{getDisplayLabel(value)}</ChipText>
            <ChipRemoveIcon
              src={redCrossIcon}
              onClick={(e) => handleRemove(value, e)}
              role="button"
              aria-label={`Remove ${getDisplayLabel(value)}`}
            />
          </Chip>
        ))}
      </ChipsContainer>
    );
  };

  return (
    <Container>
      <SelectionBox
        ref={triggerRef}
        onClick={handleOpenToggle}
        $disabled={disabled}
        $hasSelection={selectedValues.length > 0}
        $alwaysWhite={alwaysWhite}
        $triggerPadding={triggerPadding}
        $triggerWidth={triggerWidth}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={placeholder}
      >
        <TriggerInner>
          {leadingIcon ? (
            <img src={leadingIcon} alt="" style={{ width: 16, height: 16, flexShrink: 0 }} />
          ) : null}
          <TriggerLabelSlot>{renderTriggerLabel()}</TriggerLabelSlot>
        </TriggerInner>
        <DropdownArrow $isOpen={isOpen} />
      </SelectionBox>
      {isOpen &&
        createPortal(
          <DropdownPanel
            ref={dropdownRef}
            role="presentation"
            style={{
              top: dropdownPos.top,
              left: dropdownPos.left,
              width: dropdownPos.width,
              minWidth: minDropdownWidth,
            }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            {showHeader && (
              <DropdownHeader $bg={headerBackground}>
                {dropdownHeader ? <DropdownHeaderTitle>{dropdownHeader}</DropdownHeaderTitle> : null}
                {dropdownSubtitle ? <DropdownHeaderSubtitle>{dropdownSubtitle}</DropdownHeaderSubtitle> : null}
              </DropdownHeader>
            )}
            {showSearch && (
              <SearchInput
                ref={searchInputRef}
                type="search"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            )}
            <DropdownContent $withSearch={showSearch}>
              {showAllOption && !searchQuery.trim() && (
                <Row
                  type="button"
                  $selected={allSelected}
                  onClick={handleToggleAll}
                >
                  {showCheckboxes &&
                    (singleSelect ? (
                      <RadioUi $checked={allSelected} aria-hidden>
                        <RadioDot $checked={allSelected} />
                      </RadioUi>
                    ) : (
                      <CheckboxUi $checked={allSelected} aria-hidden>
                        {allSelected ? <CheckIcon /> : null}
                      </CheckboxUi>
                    ))}
                  <RowText title={allLabel}>{allLabel}</RowText>
                  {showPreviewIcon && !(isLoadingPreview && previewLoadingTarget === 'all') && (
                    <PreviewIconButton
                      onPointerDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={handlePreviewAll}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          e.stopPropagation();
                          handlePreviewAll(e);
                        }
                      }}
                      aria-label="Preview all"
                      role="button"
                      tabIndex={0}
                    >
                      <img src={previewIcon} alt="Preview" width={20} height={20} />
                    </PreviewIconButton>
                  )}
                  {showPreviewIcon && isLoadingPreview && previewLoadingTarget === 'all' && (
                    <PreviewLoadingContainer>
                      <SmallLoader />
                    </PreviewLoadingContainer>
                  )}
                </Row>
              )}
              {groupByArchived && groupedOptions && !searchQuery.trim() ? (
                <>
                  {/* Active Classes Group */}
                  {groupedOptions.active.length > 0 && (
                    <>
                      <GroupHeader
                        type="button"
                        $selected={allActiveSelected}
                        onClick={(e) => handleToggleGroup(activeValues, allActiveSelected, e)}
                      >
                        {showCheckboxes && (
                          <CheckboxUi $checked={allActiveSelected} aria-hidden>
                            {allActiveSelected ? <CheckIcon /> : null}
                          </CheckboxUi>
                        )}
                        <GroupHeaderText>{activeLabel}</GroupHeaderText>
                      </GroupHeader>
                      {groupedOptions.active.map((option, index) => {
                        const value = getOptionValue(option);
                        const selected = isValueSelected(value);
                        const visuallyChecked = singleSelect
                          ? selected
                          : allDeselected
                            ? false
                            : selectedValues.length === 0
                              ? true
                              : selected;
                        return (
                          <Row
                            type="button"
                            key={`active-${String(value)}-${index}`}
                            $selected={visuallyChecked}
                            onClick={(e) => handleToggleOption(option, e)}
                            style={{ paddingLeft: '40px' }}
                          >
                            {showCheckboxes && (
                              <CheckboxUi $checked={visuallyChecked} aria-hidden>
                                {visuallyChecked ? <CheckIcon /> : null}
                              </CheckboxUi>
                            )}
                            <RowText title={getOptionLabel(option)}>{getOptionLabel(option)}</RowText>
                          </Row>
                        );
                      })}
                    </>
                  )}

                  {/* Archived Classes Group */}
                  {groupedOptions.archived.length > 0 && (
                    <>
                      <GroupHeader
                        type="button"
                        $selected={allArchivedSelected}
                        onClick={(e) => handleToggleGroup(archivedValues, allArchivedSelected, e)}
                      >
                        {showCheckboxes && (
                          <CheckboxUi $checked={allArchivedSelected} aria-hidden>
                            {allArchivedSelected ? <CheckIcon /> : null}
                          </CheckboxUi>
                        )}
                        <GroupHeaderText>{archivedLabel}</GroupHeaderText>
                      </GroupHeader>
                      {groupedOptions.archived.map((option, index) => {
                        const value = getOptionValue(option);
                        const selected = isValueSelected(value);
                        const visuallyChecked = singleSelect
                          ? selected
                          : allDeselected
                            ? false
                            : selectedValues.length === 0
                              ? true
                              : selected;
                        return (
                          <Row
                            type="button"
                            key={`archived-${String(value)}-${index}`}
                            $selected={visuallyChecked}
                            onClick={(e) => handleToggleOption(option, e)}
                            style={{ paddingLeft: '40px' }}
                          >
                            {showCheckboxes && (
                              <CheckboxUi $checked={visuallyChecked} aria-hidden>
                                {visuallyChecked ? <CheckIcon /> : null}
                              </CheckboxUi>
                            )}
                            <RowText title={getOptionLabel(option)}>{getOptionLabel(option)}</RowText>
                          </Row>
                        );
                      })}
                    </>
                  )}
                  {groupedOptions.active.length === 0 && groupedOptions.archived.length === 0 && (
                    <NoOptions>No options available</NoOptions>
                  )}
                </>
              ) : filteredOptions.length > 0 ? (
                filteredOptions.map((option, index) => {
                  const value = getOptionValue(option);
                  const selected = isValueSelected(value);
                  const visuallyChecked = singleSelect
                    ? selected
                    : allDeselected
                      ? false
                      : selectedValues.length === 0
                        ? true
                        : selected;
                  return (
                    <Row
                      type="button"
                      key={`${String(value)}-${index}`}
                      $selected={visuallyChecked}
                      onClick={(e) => handleToggleOption(option, e)}
                    >
                      {showCheckboxes &&
                        (singleSelect ? (
                          <RadioUi $checked={visuallyChecked} aria-hidden>
                            <RadioDot $checked={visuallyChecked} />
                          </RadioUi>
                        ) : (
                          <CheckboxUi $checked={visuallyChecked} aria-hidden>
                            {visuallyChecked ? <CheckIcon /> : null}
                          </CheckboxUi>
                        ))}
                      <RowText title={getOptionLabel(option)}>{getOptionLabel(option)}</RowText>
                      {showPreviewIcon && !(isLoadingPreview && previewLoadingTarget === String(getPreviewId(option, previewType))) && (
                        <PreviewIconButton
                          onPointerDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onClick={(e) => handlePreviewOption(option, e)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              e.stopPropagation();
                              handlePreviewOption(option, e);
                            }
                          }}
                          aria-label={`Preview ${getOptionLabel(option)}`}
                          role="button"
                          tabIndex={0}
                        >
                          <img src={previewIcon} alt="Preview" width={20} height={20} />
                        </PreviewIconButton>
                      )}
                      {showPreviewIcon && isLoadingPreview && previewLoadingTarget === String(getPreviewId(option, previewType)) && (
                        <PreviewLoadingContainer>
                          <SmallLoader />
                        </PreviewLoadingContainer>
                      )}
                    </Row>
                  );
                })
              ) : (
                <NoOptions>{searchQuery ? 'No matching options' : 'No options available'}</NoOptions>
              )}
            </DropdownContent>
          </DropdownPanel>,
          document.body
        )}
      {showPreviewDialog && previewData && (
        <>
            {previewType === 'rubric' && (
              <PreviewDialog
                setMarkingCriteriaPreviewDialog={setShowPreviewDialog}
                markingCriterias={previewData}
                showEditButton={false}
              />
            )}
            {previewType === 'commentbank' && (
              <CommentBankDialog
                commentBank={previewData}
                setCommentBankPreviewDialog={setShowPreviewDialog}
                showEditButton={false}
              />
            )}
            {previewType === 'successcriteria' && (
              <SuccessCriteriaDialog
                successCriteria={previewData}
                setSuccessCriteriaPreviewDialog={setShowPreviewDialog}
                showEditButton={false}
              />
            )}
            {previewType === 'shortanswer' && (
              <ShortAnswerDialog
                shortAnswer={previewData}
                setShortAnswerPreviewDialog={setShowPreviewDialog}
                showEditButton={false}
              />
            )}
          </>
      )}
    </Container>
  );
}

function CheckIcon() {
  return (
    <CheckSvg viewBox="0 0 10 8" fill="none" aria-hidden>
      <path d="M1 4l2.5 2.5L9 1" stroke="#ffffff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </CheckSvg>
  );
}

const CI_PURPLE = '#7200e0';
const CI_ROW_SELECTED = '#f1e6fc';
const CI_HEADER_BG = '#f7f7f8';

const Container = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
`;

const TriggerInner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
`;

const TriggerLabelSlot = styled.div`
  flex: 1;
  min-width: 0;
`;

const SelectionBox = styled.div`
  display: flex;
  align-items: center;
  padding: ${({ $triggerPadding }) => $triggerPadding || '8px 12px'};
  padding-right: 32px;
  border: 1px solid #c9c6cc;
  border-radius: 8px;
  background: ${({ $hasSelection, $alwaysWhite }) =>
    $hasSelection || $alwaysWhite ? '#ffffff' : 'var(--color-neutral-80, #f2f1f3)'};
  height: var(--ci-filter-control-height, 40px);
  min-height: var(--ci-filter-control-height, 40px);
  max-height: var(--ci-filter-control-height, 40px);
  cursor: pointer;
  box-sizing: border-box;
  position: relative;
  width: 100%;
  min-width: 0;
  ${({ $triggerWidth }) => $triggerWidth && `width: ${$triggerWidth};`}
  ${({ $disabled }) =>
    $disabled &&
    `
    opacity: 0.6;
    cursor: not-allowed;
    pointer-events: none;
  `}

  @media (max-width: 480px) {
    padding: 8px 10px;
    padding-right: 28px;
  }
`;

const SelectTrigger = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: ${({ $triggerFontSize }) => $triggerFontSize || '14px'};
  color: #9ca3af;
  flex: 1;
  min-width: 0;
  max-width: 100%;
  line-height: 1.35;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  @media (max-width: 480px) {
    font-size: ${({ $triggerFontSize }) =>
      $triggerFontSize === '14px' || !$triggerFontSize ? '13px' : $triggerFontSize
    };
  }
`;

const DropdownArrow = styled.span`
  position: absolute;
  right: 12px;
  top: 0;
  bottom: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  pointer-events: none;

  &::before {
    content: '';
    border: solid #6b7280;
    border-width: 0 2px 2px 0;
    display: inline-block;
    padding: 3px;
    transform: ${(props) =>
      props.$isOpen ? 'translateY(-1px) rotate(-135deg)' : 'translateY(-1px) rotate(45deg)'};
    transition: transform 0.2s;
  }
`;

const DropdownPanel = styled.div`
  position: fixed;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  margin-top: 4px;
  max-height: min(420px, 70vh);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 2147483646;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.16);
  box-sizing: border-box;

  @media (max-width: 768px) {
    max-width: calc(100vw - 16px);
    left: 8px !important;
    right: 8px;
    width: auto !important;
  }

  @media (max-width: 480px) {
    max-width: calc(100vw - 16px);
    left: 8px !important;
    right: 8px;
    width: auto !important;
  }
`;

const DropdownHeader = styled.div`
  background: ${(props) => props.$bg || CI_HEADER_BG};
  padding: 16px 16px 12px;
  border-bottom: 1px solid #ece9f0;
  border-radius: 10px 10px 0 0;
  flex-shrink: 0;

  @media (max-width: 480px) {
    padding: 12px 12px 10px;
  }
`;

const DropdownHeaderTitle = styled.div`
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.2;
  color: #262128;
  margin-bottom: 4px;

  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const DropdownHeaderSubtitle = styled.div`
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.3;
  color: #7b7382;

  @media (max-width: 480px) {
    font-size: 13px;
  }
`;

const SearchInput = styled.input`
  width: calc(100% - 24px);
  padding: 12px 42px 12px 14px;
  margin: 12px 12px 0;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 14px;
  color: #374151;
  outline: none;
  box-sizing: border-box;
  background: #ffffff;
  background-image: url(${search14grayIcon});
  background-repeat: no-repeat;
  background-position: right 14px center;
  background-size: 14px 14px;

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    border-color: #9ca3af;
  }

  @media (max-width: 480px) {
    padding: 10px 36px 10px 12px;
    font-size: 13px;
    background-position: right 12px center;
  }
`;

const DropdownContent = styled.div`
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  padding-top: ${({ $withSearch }) => ($withSearch ? '12px' : '0')};
`;

const Row = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  margin: 0;
  border: none;
  cursor: pointer;
  text-align: left;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 15px;
  line-height: 1.35;
  color: ${(props) => (props.$selected ? CI_PURPLE : '#262128')};
  font-weight: ${(props) => (props.$selected ? 600 : 400)};
  background: ${(props) => (props.$selected ? CI_ROW_SELECTED : '#ffffff')};
  transition: background 0.12s, color 0.12s;

  &:hover {
    background: ${(props) => (props.$selected ? CI_ROW_SELECTED : '#f9fafb')};
  }

  @media (max-width: 480px) {
    padding: 10px 12px;
    gap: 8px;
    font-size: 14px;
  }
`;

const RowText = styled.span`
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CheckboxUi = styled.span`
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 2px solid ${CI_PURPLE};
  background: ${(props) => (props.$checked ? CI_PURPLE : '#ffffff')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
`;

const RadioUi = styled.span`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid ${CI_PURPLE};
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
`;

const RadioDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${CI_PURPLE};
  transform: scale(${(props) => (props.$checked ? 1 : 0)});
  opacity: ${(props) => (props.$checked ? 1 : 0)};
  transition: transform 0.12s ease, opacity 0.12s ease;
`;

const CheckSvg = styled.svg`
  width: 10px;
  height: 8px;
`;

const ChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  min-width: 0;
`;

const Chip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 12px;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 12px;
  background-color: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
  max-width: 140px;
`;

const ChipText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ChipRemoveIcon = styled.img`
  width: 14px;
  height: 14px;
  cursor: pointer;
  padding: 2px;
  border: 1px solid #e2483d;
  border-radius: 50%;
  flex-shrink: 0;
`;

const OverflowHint = styled.span`
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 12px;
  color: #000000;
  white-space: nowrap;
  flex-shrink: 0;
`;

const CompactLabelContainer = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  width: 100%;
`;

const CompactLabel = styled.span`
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: ${({ $triggerFontSize }) => $triggerFontSize || '14px'};
  color: #374151;
  line-height: 1.35;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
  max-width: 100%;
  padding-bottom: 1px;

  @media (max-width: 480px) {
    font-size: ${({ $triggerFontSize }) =>
      $triggerFontSize === '14px' || !$triggerFontSize ? '13px' : $triggerFontSize
    };
  }
`;

const NoOptions = styled.div`
  padding: 16px;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 14px;
  color: #9ca3af;
  text-align: center;
`;

const PreviewIconButton = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  margin-left: auto;
  background: none;
  cursor: pointer;
  opacity: 0.7;
  transition: all 0.2s;
  flex-shrink: 0;
  pointer-events: auto;
  position: relative;
  z-index: 1;

  &:hover {
    opacity: 1;
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }

  img {
    display: block;
    pointer-events: none;
  }
`;

const PreviewLoadingContainer = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  margin-left: auto;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
`;

const GroupHeader = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 16px;
  margin: 0;
  border: none;
  cursor: pointer;
  text-align: left;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 14px;
  line-height: 1.35;
  color: ${(props) => (props.$selected ? CI_PURPLE : '#6b7280')};
  font-weight: 600;
  background: #f9fafb;
  transition: background 0.12s, color 0.12s;
  border-top: 1px solid #e5e7eb;

  &:hover {
    background: #f3f4f6;
  }

  @media (max-width: 480px) {
    padding: 8px 12px;
    gap: 8px;
    font-size: 13px;
  }
`;

const GroupHeaderText = styled.span`
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export default DropdownMenuMultiselect;
